import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MessageSquare, Star, ThumbsUp, ThumbsDown, Minus, Building2, MapPin, Filter, CheckCircle2, Calendar, Tag, ChevronLeft, ChevronRight } from 'lucide-react';
import { KPICard } from '../components/ui/KPICard';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
// Land options
const regions = [{
  id: 'konzern',
  name: 'Alle Länder',
  icon: Building2
}, {
  id: 'deutschland',
  name: 'Deutschland'
}, {
  id: 'usa',
  name: 'USA'
}, {
  id: 'china',
  name: 'China'
}, {
  id: 'uk',
  name: 'Großbritannien'
}, {
  id: 'frankreich',
  name: 'Frankreich'
}, {
  id: 'australien',
  name: 'Australien'
}, {
  id: 'japan',
  name: 'Japan'
}];
// Topic colors and labels
const topicConfig: Record<string, {
  label: string;
  color: string;
}> = {
  wartezeit: {
    label: 'Wartezeit',
    color: 'bg-orange-100 text-orange-700'
  },
  personal: {
    label: 'Beratung',
    color: 'bg-purple-100 text-purple-700'
  },
  fahrzeugqualitaet: {
    label: 'Fahrzeugqualität',
    color: 'bg-green-100 text-green-700'
  },
  preise: {
    label: 'Preisgestaltung',
    color: 'bg-blue-100 text-blue-700'
  },
  angebot: {
    label: 'Fahrzeugangebot',
    color: 'bg-indigo-100 text-indigo-700'
  },
  sauberkeit: {
    label: 'Sauberkeit',
    color: 'bg-cyan-100 text-cyan-700'
  },
  service: {
    label: 'Service',
    color: 'bg-pink-100 text-pink-700'
  },
  werkstatt: {
    label: 'Werkstatt',
    color: 'bg-emerald-100 text-emerald-700'
  },
  parkplatz: {
    label: 'Parkplatz',
    color: 'bg-amber-100 text-amber-700'
  },
  atmosphaere: {
    label: 'Showroom',
    color: 'bg-violet-100 text-violet-700'
  },
  uebergabe: {
    label: 'Fahrzeugübergabe',
    color: 'bg-rose-100 text-rose-700'
  }
};
// Mock review data - BMW Autohäuser weltweit
const allReviews = [{
  id: 1,
  region: 'deutschland',
  store: 'BMW Niederlassung Hamburg',
  author: 'Thomas M.',
  date: '2024-01-28',
  stars: 1,
  text: 'Sehr lange Wartezeit beim Werkstatttermin, obwohl ich einen festen Termin hatte. Das Personal wirkte überfordert und wenig hilfsbereit.',
  sentiment: 'negative' as const,
  source: 'google' as const,
  topic: 'wartezeit'
}, {
  id: 2,
  region: 'usa',
  store: 'BMW of Manhattan, New York',
  author: 'Sandra K.',
  date: '2024-01-28',
  stars: 2,
  text: 'Die Beratung beim Neuwagenkauf war enttäuschend. Der Verkäufer kannte die technischen Details des iX kaum und wirkte desinteressiert.',
  sentiment: 'negative' as const,
  source: 'trustpilot' as const,
  topic: 'personal'
}, {
  id: 3,
  region: 'deutschland',
  store: 'BMW Niederlassung München',
  author: 'Michael B.',
  date: '2024-01-27',
  stars: 4,
  text: 'Tolle Beratung und schöner Showroom. Nur die Wartezeit bei der Fahrzeugübergabe war etwas länger als erwartet.',
  sentiment: 'positive' as const,
  source: 'bmw-stimme' as const,
  topic: 'uebergabe'
}, {
  id: 4,
  region: 'deutschland',
  store: 'Autohaus Fleischhauer Köln',
  author: 'Lisa W.',
  date: '2024-01-27',
  stars: 1,
  text: 'Absolut inakzeptabel! Mein Fahrzeug wurde nach der Inspektion mit einem Kratzer zurückgegeben. Keine Entschuldigung, keine Kulanz.',
  sentiment: 'negative' as const,
  source: 'google' as const,
  topic: 'werkstatt'
}, {
  id: 5,
  region: 'uk',
  store: 'BMW Park Lane, London',
  author: 'Peter H.',
  date: '2024-01-26',
  stars: 5,
  text: 'Best BMW dealership in London! Excellent consultation for the M3 purchase and a perfect handover ceremony.',
  sentiment: 'positive' as const,
  source: 'trustpilot' as const,
  topic: 'uebergabe'
}, {
  id: 6,
  region: 'deutschland',
  store: 'Autohaus Becker Dortmund',
  author: 'Anna S.',
  date: '2024-01-26',
  stars: 3,
  text: 'Durchschnittliches Autohaus. Beratung war okay, aber nicht besonders engagiert. Showroom ist etwas veraltet.',
  sentiment: 'neutral' as const,
  source: 'bmw-stimme' as const,
  topic: 'atmosphaere'
}, {
  id: 7,
  region: 'deutschland',
  store: 'BMW Niederlassung Hamburg',
  author: 'Klaus R.',
  date: '2024-01-25',
  stars: 2,
  text: 'Werkstatttermin erst nach 3 Wochen möglich. Für ein Premium-Autohaus ist das nicht akzeptabel.',
  sentiment: 'negative' as const,
  source: 'google' as const,
  topic: 'wartezeit'
}, {
  id: 8,
  region: 'china',
  store: 'BMW Brilliance Shenyang',
  author: 'Wang L.',
  date: '2024-01-25',
  stars: 5,
  text: '服务非常出色！BMW iX3的介绍非常专业，工作人员态度友好，强烈推荐！',
  sentiment: 'positive' as const,
  source: 'trustpilot' as const,
  topic: 'personal'
}, {
  id: 9,
  region: 'deutschland',
  store: 'Autohaus Hahn Stuttgart',
  author: 'Frank M.',
  date: '2024-01-24',
  stars: 4,
  text: 'Sehr gute Fahrzeugqualität und kompetente Beratung. Die Mitarbeiter nehmen sich Zeit für alle Fragen.',
  sentiment: 'positive' as const,
  source: 'bmw-stimme' as const,
  topic: 'fahrzeugqualitaet'
}, {
  id: 10,
  region: 'frankreich',
  store: 'BMW Paris Champs-Élysées',
  author: 'Sabine L.',
  date: '2024-01-24',
  stars: 3,
  text: 'Correct, mais les conditions de financement pourraient être communiquées plus clairement.',
  sentiment: 'neutral' as const,
  source: 'google' as const,
  topic: 'preise'
}, {
  id: 11,
  region: 'deutschland',
  store: 'BMW Niederlassung Kiel',
  author: 'Petra W.',
  date: '2024-01-23',
  stars: 5,
  text: 'Toller Service! Der Berater hat mir geholfen, das perfekte Fahrzeug für meine Bedürfnisse zu finden. Sehr empfehlenswert!',
  sentiment: 'positive' as const,
  source: 'trustpilot' as const,
  topic: 'service'
}, {
  id: 12,
  region: 'deutschland',
  store: 'BMW Niederlassung München',
  author: 'Hans M.',
  date: '2024-01-22',
  stars: 5,
  text: 'Beste Fahrzeugqualität und erstklassige Beratung! Mein neuer BMW M5 ist ein Traum. Die Übergabe war ein unvergessliches Erlebnis.',
  sentiment: 'positive' as const,
  source: 'bmw-stimme' as const,
  topic: 'fahrzeugqualitaet'
}, {
  id: 13,
  region: 'usa',
  store: 'BMW of Beverly Hills, Los Angeles',
  author: 'Uwe K.',
  date: '2024-01-22',
  stars: 4,
  text: 'Always reliable service and friendly staff. ConnectedDrive setup was explained very well.',
  sentiment: 'positive' as const,
  source: 'google' as const,
  topic: 'service'
}, {
  id: 14,
  region: 'japan',
  store: 'BMW Tokyo Aoyama',
  author: 'Gabi F.',
  date: '2024-01-21',
  stars: 3,
  text: 'ショールームは清潔で整っていましたが、説明が少し短かったです。',
  sentiment: 'neutral' as const,
  source: 'trustpilot' as const,
  topic: 'atmosphaere'
}, {
  id: 15,
  region: 'china',
  store: 'BMW Center Beijing',
  author: 'Martin S.',
  date: '2024-01-21',
  stars: 4,
  text: '电动车和混合动力车型选择丰富，充电基础设施的咨询非常有帮助。',
  sentiment: 'positive' as const,
  source: 'bmw-stimme' as const,
  topic: 'angebot'
}, {
  id: 16,
  region: 'deutschland',
  store: 'BMW Niederlassung Bremen',
  author: 'Heike L.',
  date: '2024-01-20',
  stars: 2,
  text: 'Werkstatt hat das Fahrzeug nicht fertig gehabt wie versprochen. Musste zweimal nachfragen.',
  sentiment: 'negative' as const,
  source: 'google' as const,
  topic: 'werkstatt'
}, {
  id: 17,
  region: 'australien',
  store: 'BMW Sydney Rushcutters Bay',
  author: 'Jens B.',
  date: '2024-01-20',
  stars: 5,
  text: 'Beautiful modern showroom with a fantastic atmosphere! Feels premium from start to finish.',
  sentiment: 'positive' as const,
  source: 'trustpilot' as const,
  topic: 'atmosphaere'
}, {
  id: 18,
  region: 'uk',
  store: 'BMW Reading, Berkshire',
  author: 'Monika R.',
  date: '2024-01-19',
  stars: 3,
  text: 'Solid dealership, does the job. Waiting time for the service was a bit long.',
  sentiment: 'neutral' as const,
  source: 'bmw-stimme' as const,
  topic: 'wartezeit'
}];
// Source icons
const GoogleIcon = () => <svg viewBox="0 0 24 24" className="w-4 h-4" data-id="element-532">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" data-id="element-533" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" data-id="element-534" />
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" data-id="element-535" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" data-id="element-536" />
  </svg>;
const TrustpilotIcon = () => <svg viewBox="0 0 24 24" className="w-4 h-4" data-id="element-537">
    <path fill="#00B67A" d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" data-id="element-538" />
  </svg>;
const BmwStimmeIcon = () => <div className="w-4 h-4 rounded-full flex items-center justify-center" style={{
  backgroundColor: '#1C69D4'
}} data-id="element-539">
    <span className="text-[8px] font-bold text-white" data-id="element-540">B</span>
  </div>;
const ITEMS_PER_PAGE = 10;
export function GeneralStimmenPage() {
  const [selectedRegion, setSelectedRegion] = useState('konzern');
  const [sentimentFilter, setSentimentFilter] = useState<'all' | 'positive' | 'neutral' | 'negative'>('all');
  const [sourceFilter, setSourceFilter] = useState<'all' | 'google' | 'trustpilot' | 'bmw-stimme'>('all');
  const [currentPage, setCurrentPage] = useState(1);
  // Filter reviews by region
  const regionReviews = selectedRegion === 'konzern' ? allReviews : allReviews.filter(r => r.region === selectedRegion);
  // Filter by sentiment and source
  const filteredReviews = regionReviews.filter(review => sentimentFilter === 'all' || review.sentiment === sentimentFilter).filter(review => sourceFilter === 'all' || review.source === sourceFilter).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  // Pagination
  const totalPages = Math.ceil(filteredReviews.length / ITEMS_PER_PAGE);
  const paginatedReviews = filteredReviews.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
  // Reset page when filters change
  const handleFilterChange = (filterFn: () => void) => {
    filterFn();
    setCurrentPage(1);
  };
  // Calculate stats
  const stats = {
    total: regionReviews.length,
    positive: regionReviews.filter(r => r.sentiment === 'positive').length,
    neutral: regionReviews.filter(r => r.sentiment === 'neutral').length,
    negative: regionReviews.filter(r => r.sentiment === 'negative').length,
    avgRating: regionReviews.reduce((acc, r) => acc + r.stars, 0) / regionReviews.length || 0
  };
  const getStarColor = (stars: number) => {
    if (stars <= 2) return 'text-[#E03C32]';
    if (stars <= 3) return 'text-[#FFD301]';
    return 'text-[#639754]';
  };
  const getSourceIcon = (source: string) => {
    switch (source) {
      case 'google':
        return <GoogleIcon data-id="element-541" />;
      case 'trustpilot':
        return <TrustpilotIcon data-id="element-542" />;
      case 'bmw-stimme':
        return <BmwStimmeIcon data-id="element-543" />;
      default:
        return null;
    }
  };
  const getSourceLabel = (source: string) => {
    switch (source) {
      case 'google':
        return 'Google';
      case 'trustpilot':
        return 'Trustpilot';
      case 'bmw-stimme':
        return 'BMW Kundenstimme';
      default:
        return source;
    }
  };
  const getSourceStyle = (source: string) => {
    switch (source) {
      case 'google':
        return 'bg-blue-50 text-blue-700';
      case 'trustpilot':
        return 'bg-green-50 text-green-700';
      case 'bmw-stimme':
        return 'bg-[#EEF4FC] text-[#1C69D4]';
      default:
        return 'bg-gray-50 text-gray-700';
    }
  };
  return <div className="p-6 space-y-6 overflow-y-auto h-[calc(100vh-64px)]" data-id="element-544">
      {/* Land Filter */}
      <Card className="!p-4" data-id="element-545">
        <div className="flex items-center gap-4 flex-wrap" data-id="element-546">
          <div className="flex items-center gap-2 text-sm font-medium text-gray-700" data-id="element-547">
            <Filter size={16} data-id="element-548" />
            <span data-id="element-549">Land:</span>
          </div>
          <div className="flex flex-wrap gap-2" data-id="element-550">
            {regions.map(region => {
            const isSelected = selectedRegion === region.id;
            const Icon = region.icon;
            return <button key={region.id} onClick={() => handleFilterChange(() => setSelectedRegion(region.id))} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${isSelected ? 'text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} style={isSelected ? {
              backgroundColor: '#1C69D4'
            } : {}} data-id="element-551">
                  {Icon && <Icon size={14} data-id="element-552" />}
                  {region.name}
                </button>;
          })}
          </div>
        </div>
      </Card>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4" data-id="element-553">
        <KPICard title="Bewertungen" value={stats.total.toString()} icon={<MessageSquare size={18} data-id="element-555" />} data-id="element-554" />
        <KPICard title="Ø Bewertung" value={stats.avgRating.toFixed(1)} icon={<Star size={18} data-id="element-557" />} data-id="element-556" />
        <KPICard title="Positiv" value={stats.positive.toString()} icon={<ThumbsUp size={18} data-id="element-559" />} variant="positive" data-id="element-558" />
        <KPICard title="Neutral" value={stats.neutral.toString()} icon={<Minus size={18} data-id="element-561" />} variant="neutral" data-id="element-560" />
        <KPICard title="Negativ" value={stats.negative.toString()} icon={<ThumbsDown size={18} data-id="element-563" />} variant="negative" data-id="element-562" />
      </div>

      {/* Filters */}
      <Card className="!p-4" data-id="element-564">
        <div className="flex items-center justify-between flex-wrap gap-4" data-id="element-565">
          <div className="flex items-center gap-3" data-id="element-566">
            <span className="text-sm font-medium text-gray-700" data-id="element-567">
              Sentiment:
            </span>
            <div className="flex gap-2" data-id="element-568">
              <button onClick={() => handleFilterChange(() => setSentimentFilter('all'))} className={`px-3 py-1.5 text-sm rounded-md transition-colors ${sentimentFilter === 'all' ? 'text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} style={sentimentFilter === 'all' ? {
              backgroundColor: '#1C69D4'
            } : {}} data-id="element-569">
                Alle ({regionReviews.length})
              </button>
              <button onClick={() => handleFilterChange(() => setSentimentFilter('positive'))} className={`px-3 py-1.5 text-sm rounded-md transition-colors flex items-center gap-1.5 ${sentimentFilter === 'positive' ? 'text-white' : 'text-[#639754] hover:opacity-80'}`} style={{
              backgroundColor: sentimentFilter === 'positive' ? '#639754' : '#e8f0e5'
            }} data-id="element-570">
                <ThumbsUp size={14} data-id="element-571" />
                Positiv ({stats.positive})
              </button>
              <button onClick={() => handleFilterChange(() => setSentimentFilter('neutral'))} className={`px-3 py-1.5 text-sm rounded-md transition-colors flex items-center gap-1.5 ${sentimentFilter === 'neutral' ? 'text-gray-800' : 'text-[#b39500] hover:opacity-80'}`} style={{
              backgroundColor: sentimentFilter === 'neutral' ? '#FFD301' : '#fff9db'
            }} data-id="element-572">
                <Minus size={14} data-id="element-573" />
                Neutral ({stats.neutral})
              </button>
              <button onClick={() => handleFilterChange(() => setSentimentFilter('negative'))} className={`px-3 py-1.5 text-sm rounded-md transition-colors flex items-center gap-1.5 ${sentimentFilter === 'negative' ? 'text-white' : 'text-[#E03C32] hover:opacity-80'}`} style={{
              backgroundColor: sentimentFilter === 'negative' ? '#E03C32' : '#fce8e7'
            }} data-id="element-574">
                <ThumbsDown size={14} data-id="element-575" />
                Negativ ({stats.negative})
              </button>
            </div>
          </div>

          <div className="flex items-center gap-3" data-id="element-576">
            <span className="text-sm font-medium text-gray-700" data-id="element-577">Quelle:</span>
            <div className="flex gap-2" data-id="element-578">
              <button onClick={() => handleFilterChange(() => setSourceFilter('all'))} className={`px-3 py-1.5 text-sm rounded-md transition-colors ${sourceFilter === 'all' ? 'text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} style={sourceFilter === 'all' ? {
              backgroundColor: '#1C69D4'
            } : {}} data-id="element-579">
                Alle
              </button>
              <button onClick={() => handleFilterChange(() => setSourceFilter('google'))} className={`px-3 py-1.5 text-sm rounded-md transition-colors flex items-center gap-1.5 ${sourceFilter === 'google' ? 'bg-blue-600 text-white' : 'bg-blue-50 text-blue-700 hover:bg-blue-100'}`} data-id="element-580">
                <GoogleIcon data-id="element-581" />
                Google
              </button>
              <button onClick={() => handleFilterChange(() => setSourceFilter('trustpilot'))} className={`px-3 py-1.5 text-sm rounded-md transition-colors flex items-center gap-1.5 ${sourceFilter === 'trustpilot' ? 'bg-green-600 text-white' : 'bg-green-50 text-green-700 hover:bg-green-100'}`} data-id="element-582">
                <TrustpilotIcon data-id="element-583" />
                Trustpilot
              </button>
              <button onClick={() => handleFilterChange(() => setSourceFilter('bmw-stimme'))} className={`px-3 py-1.5 text-sm rounded-md transition-colors flex items-center gap-1.5 ${sourceFilter === 'bmw-stimme' ? 'text-white' : 'text-[#1C69D4] hover:opacity-80'}`} style={{
              backgroundColor: sourceFilter === 'bmw-stimme' ? '#1C69D4' : '#EEF4FC'
            }} data-id="element-584">
                <BmwStimmeIcon data-id="element-585" />
                BMW Kundenstimme
              </button>
            </div>
          </div>
        </div>
      </Card>

      {/* Section Title */}
      <div className="flex items-center justify-between" data-id="element-586">
        <h2 className="text-lg font-semibold text-gray-900" data-id="element-587">
          Alle weltweiten Bewertungen
        </h2>
        <span className="text-sm text-gray-500" data-id="element-588">
          {filteredReviews.length} Bewertungen gefunden
        </span>
      </div>

      {/* Reviews List */}
      <div className="space-y-3" data-id="element-589">
        {paginatedReviews.length === 0 ? <Card className="!p-8 text-center" data-id="element-590">
            <p className="text-gray-500" data-id="element-591">
              Keine Bewertungen für diese Filter gefunden.
            </p>
          </Card> : paginatedReviews.map((review, index) => <motion.div key={review.id} initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: index * 0.03
      }} data-id="element-592">
              <Card className="!p-0 overflow-hidden" data-id="element-593">
                <div className="flex" data-id="element-594">
                  <div className="w-1.5" style={{
              backgroundColor: review.sentiment === 'positive' ? '#639754' : review.sentiment === 'neutral' ? '#FFD301' : '#E03C32'
            }} data-id="element-595" />
                  <div className="flex-1 p-4" data-id="element-596">
                    <div className="flex items-start justify-between gap-4" data-id="element-597">
                      <div className="flex-1 min-w-0" data-id="element-598">
                        <div className="flex items-center gap-3 mb-2 flex-wrap" data-id="element-599">
                          <div className="flex items-center gap-1" data-id="element-600">
                            {[...Array(5)].map((_, i) => <Star key={i} size={14} className={i < review.stars ? getStarColor(review.stars) : 'text-gray-200'} fill={i < review.stars ? 'currentColor' : 'none'} data-id="element-601" />)}
                          </div>
                          <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${topicConfig[review.topic]?.color || 'bg-gray-100 text-gray-600'}`} data-id="element-602">
                            <Tag size={10} data-id="element-603" />
                            {topicConfig[review.topic]?.label || review.topic}
                          </div>
                          <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${getSourceStyle(review.source)}`} data-id="element-604">
                            {getSourceIcon(review.source)}
                            {getSourceLabel(review.source)}
                          </div>
                          <span className="text-sm text-gray-500" data-id="element-605">
                            {review.author}
                          </span>
                          <span className="text-sm text-gray-400" data-id="element-606">•</span>
                          <span className="text-sm text-gray-500" data-id="element-607">
                            {new Date(review.date).toLocaleDateString('de-DE')}
                          </span>
                        </div>
                        <div className="text-xs text-gray-400 mb-2" data-id="element-608">
                          {review.store}
                        </div>
                        <p className="text-sm text-gray-700" data-id="element-609">{review.text}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            </motion.div>)}
      </div>

      {/* Pagination */}
      {totalPages > 1 && <div className="flex items-center justify-center gap-2 pt-4" data-id="element-610">
          <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" data-id="element-611">
            <ChevronLeft size={18} data-id="element-612" />
          </button>
          <div className="flex items-center gap-1" data-id="element-613">
            {Array.from({
          length: totalPages
        }, (_, i) => i + 1).map(page => <button key={page} onClick={() => setCurrentPage(page)} className={`w-9 h-9 rounded-lg text-sm font-medium transition-colors ${currentPage === page ? 'text-white' : 'hover:bg-gray-100 text-gray-600'}`} style={currentPage === page ? {
          backgroundColor: '#1C69D4'
        } : {}} data-id="element-614">
                {page}
              </button>)}
          </div>
          <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" data-id="element-615">
            <ChevronRight size={18} data-id="element-616" />
          </button>
        </div>}
    </div>;
}